// context/AuthContext.js
import axios from "axios";
import {
  AUTH_STORAGE_KEYS,
  UNAUTHORIZED_ROLE_MESSAGE,
  getRolePermissions,
  hasResourceAccess,
  isAllowedLoginRole,
  normalizeRole,
} from "constants/accessControl";
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(() => localStorage.getItem("token"));
  const [loading, setLoading] = useState(true);
  const [role, setRole] = useState(() => normalizeRole(localStorage.getItem("role")) || null);
  const [kycLevel, setKycLevel] = useState(user?.kycLevel || 0);
  const [error, setError] = useState(null);
  const [kycError, setKycError] = useState(null);
  const [businessValidationError, setBusinessValidationError] = useState(null);

  const isAuthenticated = () => Boolean(token);

  const clearStoredSession = useCallback(() => {
    AUTH_STORAGE_KEYS.forEach((key) => localStorage.removeItem(key));
  }, []);

  const syncStorageValue = useCallback((key, value) => {
    if (value === undefined || value === null || value === "") {
      localStorage.removeItem(key);
      return;
    }

    localStorage.setItem(key, String(value));
  }, []);

  const clearSessionState = useCallback(() => {
    setToken(null);
    setUser(null);
    setRole(null);
    setError(null);
    setKycLevel(0);
  }, []);

  const applyAuthenticatedSession = useCallback(
    (userData, providedToken = userData?.token) => {
      const nextRole = normalizeRole(userData?.role);
      const nextToken = providedToken || null;

      if (!isAllowedLoginRole(nextRole)) {
        clearStoredSession();
        clearSessionState();
        setLoading(false);
        setError(UNAUTHORIZED_ROLE_MESSAGE);
        throw new Error(UNAUTHORIZED_ROLE_MESSAGE);
      }

      if (nextToken) {
        localStorage.setItem("token", nextToken);
      } else {
        localStorage.removeItem("token");
      }

      localStorage.setItem("role", nextRole || "");
      syncStorageValue("userId", userData?.id);
      syncStorageValue("firstName", userData?.firstName);
      syncStorageValue("lastName", userData?.lastName);
      syncStorageValue("email", userData?.email);
      syncStorageValue("phone", userData?.phone);
      syncStorageValue("balance", userData?.balance);
      syncStorageValue("lastLoginDate", userData?.lastLoginDate);
      syncStorageValue("isActive", userData?.isActive);
      syncStorageValue("isTwoFaSetup", userData?.isTwoFaSetup);
      syncStorageValue("isTwoFaEnabled", userData?.isTwoFaEnabled);

      setToken(nextToken);
      setUser(userData || null);
      setRole(nextRole || null);
      setKycLevel(userData?.kycLevel || 0);
      setError(null);

      return userData;
    },
    [clearSessionState, clearStoredSession, syncStorageValue]
  );

  const logout = useCallback(() => {
    clearStoredSession();
    clearSessionState();
  }, [clearSessionState, clearStoredSession]);

  const login = async (email, password) => {
    try {
      setLoading(true);
      setError(null);

      const response = await axios.post(
        "https://merchantapex.fairsurepay.com/api/authenticate",
        {
          email,
          password,
        }
      );
      const userData = response.data.data;
      return applyAuthenticatedSession(userData, userData?.token);
    } catch (err) {
      console.error("Login Error", err);
      setError(err?.response?.data?.errorMessage || err?.message || "Login failed");
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const updatePassword = async (payload) => {
    try {
      // Use a local operation for password updates so we don't toggle
      // the global auth `loading` flag (which is used for route protection).
      setError(null);

      const response = await axios.post("/api/user/change-password", payload, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      // Handle success response
      toast.success("Password Update Successful");
      return response.data;
    } catch (error) {
      // console.error(error);
      setError(error?.response?.data?.message || "An Error Occurred");
      toast.error("Password update failed.");
      throw error;
    }
  };

  const updateMerchantPersonal = async (payload) => {
    try {
      setError(null);
      const response = await axios.post(
        "/api/merchant/edit-basic-info",
        payload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      const userData = response.data.data;
      setUser(userData);
      return userData;
    } catch (error) {
      console.log("Error updating merchant personal info:", error);
      setError(error?.response?.data?.message || "Update failed");
      throw error;
    }
  };

  const addMerchantBusiness = async (data) => {
    try {
      setError(null);
      // console.log("data passed to addMerchant", data);

      const businessType = data.businessType;

      const formDataToSubmit = { ...data, bvn: Number(data.bvn) };
      delete formDataToSubmit.businessTypeLabel;
      delete formDataToSubmit.businessType;

      // console.log("formData to be submitted", formDataToSubmit);

      const response = await axios.post(
        `/api/merchant/merchant-profiling/${businessType}`,
        formDataToSubmit,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const userData = response.data.data;
      setUser(userData);
      toast.success("Business Info Saved");
      return userData;
    } catch (error) {
      console.log("Error updating merchant Business info:", error);
      setError(error?.response?.data?.message || "Update failed");
      throw error;
    }
  };

  // ---------- KYC ------
  const fetchMerchantKycDocs = async (data) => {
    // if (!hasToken) return;

    setLoading(true);
    setKycError(null);

    try {
      // console.log(
      //   "uploadKycDocs: uploading documents, keys:",
      //   Object.keys(data || {})
      // );
      const res = await axios.post(`/api/merchant/upload/bulk`, data, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data",
        },
      });

      setUser(res.data.merchantInfo);
      setKycLevel(res.data?.merchantInfo?.kycLevel || 1);

      // console.log("uploadKycDocs: response:", res && res.data ? res.data : res);

      return res;
    } catch (error) {
      console.error("uploadKycDocs error:", error);
      const msg = error?.response?.data?.message || "Error uploading documents";
      setKycError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  // ----- ACCEPT KYC -------
  // ----- REJECT KYC -------

  // Updated validateBusiness function that uses the response from the API
  const validateBusiness = async (data) => {
    try {
      const formDataToSubmit = { ...data };
      delete formDataToSubmit.businessTypeLabel;
      delete formDataToSubmit.businessType;

      const response = await axios.post(
        "/api/register/validate-business",
        formDataToSubmit
      );
      const resData = response.data.data;

      // console.log("Validation response:", response.data);
      const errors = [];

      // Check if the field is available, if not, add error
      resData.forEach((item) => {
        if (!item.available) {
          errors.push({
            field: item.field,
            message: item.message,
          });
        }
      });

      // console.log(errors);
      // Optionally add other validations here, like checking if businessName is too short, etc.
      if (data.businessName && data.businessName.length < 3) {
        errors.push({
          field: "businessName",
          message: "Business name must be at least 3 characters",
        });
      }

      if (data.businessAddress && data.businessAddress.length < 10) {
        errors.push({
          field: "businessAddress",
          message: "Please provide a complete address",
        });
      }

      if (data.bvn && !/^\d{9}$/.test(data.bvn)) {
        errors.push({
          field: "bvn",
          message: "BVN must be 9 digits",
        });
      }

      if (data.businessPhone && !/^[\d+\-\s()]+$/.test(data.businessPhone)) {
        errors.push({
          field: "businessPhone",
          message: "Invalid phone number format",
        });
      }

      if (
        data.businessEmail &&
        !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.businessEmail)
      ) {
        errors.push({
          field: "businessEmail",
          message: "Invalid email format",
        });
      }

      if (data.regBusinessNo && data.regBusinessNo.length < 5) {
        errors.push({
          field: "regBusinessNo",
          message: "Invalid registration number",
        });
      }

      // Return the errors
      setBusinessValidationError(errors);
      return errors;
    } catch (error) {
      console.error("Error during business validation:", error);
      return [];
    }
  };

  // Rehydrate session on reload
  useEffect(() => {
    const initAuth = async () => {
      try {
        const storedToken = localStorage.getItem("token");
        if (!storedToken) {
          setLoading(false);
          return;
        }

        // only update state if not already set
        if (!token) setToken(storedToken);

        const res = await axios.get("/api/admin/merchant/list-admin-info", {
          headers: {
            Authorization: `Bearer ${storedToken}`,
          },
        });

        const userData = res.data.data;
        applyAuthenticatedSession(userData, storedToken);
      } catch (err) {
        console.error("Error restoring session:", err);
        logout();
      } finally {
        setLoading(false);
      }
    };

    initAuth();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const permissions = useMemo(() => getRolePermissions(role), [role]);
  const hasAccess = useCallback((resource) => hasResourceAccess(role, resource), [role]);
  const sessionKey =
    token && role ? `${user?.id || user?.email || "anonymous"}:${role}:${token}` : null;

  const value = useMemo(
    () => ({
      user,
      token,
      role,
      permissions,
      sessionKey,
      kycLevel,
      loading,

      error,
      kycError,
      businessValidationError,

      login,
      updatePassword,
      logout,
      isAuthenticated,
      hasAccess,
      applyAuthenticatedSession,

      validateBusiness,
      updateMerchantPersonal,
      addMerchantBusiness,
      fetchMerchantKycDocs,
    }),
    [
      user,
      token,
      role,
      permissions,
      sessionKey,
      kycLevel,
      loading,
      error,
      kycError,
      businessValidationError,
      login,
      updatePassword,
      logout,
      hasAccess,
      applyAuthenticatedSession,
      validateBusiness,
      updateMerchantPersonal,
      addMerchantBusiness,
      fetchMerchantKycDocs,
      isAuthenticated,
    ]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
